import { Injectable } from '@angular/core';
import { Observable, Subject, interval } from 'rxjs';
import { delay, filter } from 'rxjs/operators';
import { SERVICE_WORKER_FILE_NAME } from '../shared/constants';
import { ApplyUpdatesMessage, SwInstalledMessage, SwMessage } from '../shared/messages';
import { SwConfig } from './types';

@Injectable()
export class UpdatesHandlerService {
  private serviceWorkerRegistration: ServiceWorkerRegistration;
  private _activeSwExists: boolean;
  private updateExistsSubject = new Subject<void>();
  private updateActivatedSubject = new Subject<void>();
  private updateInstalledSubject = new Subject<void>();

  get activeSwExists(): boolean {
    return this._activeSwExists;
  }

  readonly updateInstalled$: Observable<any> = this.updateInstalledSubject.pipe(
    filter(() => this.activeSwExists)
  );
  readonly updateActivated$: Observable<any> = this.updateActivatedSubject.asObservable();
  readonly updateExists$: Observable<any> = this.updateExistsSubject.asObservable();

  async initAsync(swConfig: SwConfig): Promise<void> {
    if ('serviceWorker' in navigator) {
      await this.installServiceWorker();
      this.setupEvents();
      this.handleInitialNavigationAsync();

      interval(swConfig.pollingInterval * 1000).subscribe(async () => {
        const currentRegistration = await this.getSwRegistrationAsync();

        await currentRegistration?.update();
      });
    }
  }

  async activateUpdateAsync(): Promise<void> {
    const currentRegistration = await this.getSwRegistrationAsync();
    currentRegistration.waiting?.postMessage(new ApplyUpdatesMessage());
  }

  private async getSwRegistrationAsync(): Promise<ServiceWorkerRegistration> {
    const registrations = await navigator.serviceWorker.getRegistrations();
    return registrations[0];
  }

  private async installServiceWorker(): Promise<void> {
    const registrations = await navigator.serviceWorker.getRegistrations();

    for (const reg of registrations) {
      if (reg?.active?.scriptURL?.endsWith('ngsw-worker.js')) {
        reg.unregister();
      }
    }

    this.serviceWorkerRegistration = registrations.find((reg) =>
      reg?.active?.scriptURL?.endsWith(SERVICE_WORKER_FILE_NAME),
    ) as ServiceWorkerRegistration;

    this._activeSwExists = !!this.serviceWorkerRegistration;

    if (!this.serviceWorkerRegistration) {
      this.serviceWorkerRegistration = await navigator.serviceWorker.register(SERVICE_WORKER_FILE_NAME);
      this.updateExistsSubject.next();
    }

    await navigator.serviceWorker.ready;
  }

  private setupEvents(): void {
    this.serviceWorkerRegistration.addEventListener('updatefound', () => this.updateExistsSubject.next());
    navigator.serviceWorker.addEventListener('controllerchange', () => this.updateActivatedSubject.next());
    navigator.serviceWorker.addEventListener('message', (event) => {
      const swMessage: SwMessage = event.data;

      switch (swMessage?.type) {
        case SwInstalledMessage.messageType: this.updateInstalledSubject.next();
      }
    });
    this.updateActivated$.pipe(delay(1000)).subscribe(() => {
      this._activeSwExists = true;
    });
  }

  private async handleInitialNavigationAsync(): Promise<void> {
    if (this._activeSwExists) {
      await this.serviceWorkerRegistration.update();
    }

    const swRegistration = await this.getSwRegistrationAsync();

    if (swRegistration.waiting) {
      // wee need to delay firing the event when there is waiting SW on initial navigation
      setTimeout(() => {
        this.updateInstalledSubject.next();
      }, 1000);
    }
  }
}
